import { useState, useEffect, useMemo, useCallback } from "react";
import { useForm, type FormData } from "../context/FormContext";
import { useLang } from "../context/LanguageContext";
import {
  buildMergedUploadedSections,
  getCategoryUploadStatus,
  isCategoryUploadComplete,
  resolveQualifyingRoute,
  shouldShowEmploymentDocCategory,
  shouldShowFamilyDocCategory,
  type DocChecklistCategory,
  type DocumentSectionAckKey,
} from "../utils/deriveDocumentChecklistHints";
import DocUpload from "./documents/DocUpload";
import DocumentFlowHelpSheet from "./DocumentFlowHelpSheet";

type DocCategory =
  | "identity"
  | "residence"
  | "criminal"
  | "asylum"
  | "employment"
  | "family"
  | "vulnerability"
  | "other"
  | "denial";

const DOC_CATEGORY_LABEL_KEYS: Record<DocCategory, string> = {
  identity: "docFlow.catIdentity",
  residence: "docFlow.catResidence",
  criminal: "docFlow.catCriminal",
  asylum: "docFlow.catAsylum",
  employment: "docFlow.catEmployment",
  family: "docFlow.catFamily",
  vulnerability: "docFlow.catVulnerability",
  other: "docFlow.catOther",
  denial: "docFlow.catDenial",
};

function docCategoryToChecklist(cat: DocCategory): DocChecklistCategory {
  return cat;
}

function getCategoryStatus(
  cat: DocCategory,
  data: ReturnType<typeof useForm>["data"]
): "notStarted" | "inProgress" | "complete" {
  const checklistCat = docCategoryToChecklist(cat);
  return getCategoryUploadStatus(checklistCat, buildMergedUploadedSections(data), data);
}

function isCategoryComplete(cat: DocCategory, data: ReturnType<typeof useForm>["data"]): boolean {
  return getCategoryStatus(cat, data) === "complete";
}

function formDataWithSectionUnacked(data: FormData, section: DocumentSectionAckKey): FormData {
  return {
    ...data,
    documentSectionAcknowledged: {
      ...data.documentSectionAcknowledged,
      [section]: false,
    },
    proofOfResidenceAcknowledged:
      section === "residence" ? false : data.proofOfResidenceAcknowledged,
  };
}

/** Next incomplete category after un-acking (wraps); null ⇒ all complete → leave to organizer. */
function pickNextCategoryAfterSaveAndFinish(
  categories: DocCategory[],
  current: DocCategory,
  data: FormData,
  unacked: DocumentSectionAckKey
): DocCategory | null {
  const adjusted = formDataWithSectionUnacked(data, unacked);
  const idx = categories.indexOf(current);
  for (let i = idx + 1; i < categories.length; i++) {
    if (!isCategoryUploadComplete(categories[i], adjusted)) {
      return categories[i];
    }
  }
  for (let i = 0; i < idx; i++) {
    if (!isCategoryUploadComplete(categories[i], adjusted)) {
      return categories[i];
    }
  }
  return null;
}

export default function DocumentFlow({ onBack, initialCategory }: { onBack: () => void; initialCategory?: string | null }) {
  const { data } = useForm();
  const { t, isRTL } = useLang();
  const [category, setCategory] = useState<DocCategory>("residence");
  const [helpSheetOpen, setHelpSheetOpen] = useState(false);
  const qualifyingRoute = resolveQualifyingRoute(data.qualifyingGround, data.subRoute, data.irregularSubRoute);
  const isAsylumApplied = String(data.asylumApplicant || "").trim().toLowerCase() === "yes" || data.asylumRoutingConfirmed === true;
  const categories = useMemo<DocCategory[]>(() => {
    const next: DocCategory[] = ["identity", "residence", "criminal"];
    if (isAsylumApplied) {
      next.push("asylum");
    } else {
      if (shouldShowEmploymentDocCategory(qualifyingRoute)) {
        next.push("employment");
      } else if (shouldShowFamilyDocCategory(qualifyingRoute)) {
        next.push("family");
      } else if (qualifyingRoute === "vulnerability") {
        next.push("vulnerability");
      }
    }
    next.push("other");
    return next;
  }, [isAsylumApplied, qualifyingRoute]);

  const firstIncompleteCategory =
    categories.find((cat) => cat !== "other" && !isCategoryComplete(cat, data)) ||
    categories.find((cat) => !isCategoryComplete(cat, data)) ||
    categories[0];

  useEffect(() => {
    if (initialCategory) {
      setCategory(initialCategory as DocCategory);
    }
  }, [initialCategory]);

  const [initialCategoryApplied, setInitialCategoryApplied] = useState(false);
  useEffect(() => {
    if (!initialCategoryApplied && !initialCategory && firstIncompleteCategory) {
      setCategory(firstIncompleteCategory);
      setInitialCategoryApplied(true);
    }
  }, [firstIncompleteCategory, initialCategory, initialCategoryApplied]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [category]);

  const currentCategoryTitle = useMemo(
    () => t(DOC_CATEGORY_LABEL_KEYS[category] as Parameters<typeof t>[0]),
    [category, t]
  );

  const handleDone = () => {
    const currentIndex = categories.indexOf(category);
    const nextIncomplete = categories.find((cat, i) => {
      if (i <= currentIndex) return false;
      return !isCategoryComplete(cat, data);
    });

    if (nextIncomplete) {
      setCategory(nextIncomplete);
    } else {
      onBack();
    }
  };

  const handleSaveAndFinishLater = useCallback(
    (unacked: DocumentSectionAckKey) => {
      const next = pickNextCategoryAfterSaveAndFinish(categories, category, data, unacked);
      if (next) {
        setCategory(next);
      } else {
        onBack();
      }
    },
    [categories, category, data, onBack]
  );

  return (
    <div className="relative w-full min-w-0 overflow-x-hidden">
      <DocUpload
        category={category}
        onDone={handleDone}
        onBack={onBack}
        onSaveAndFinishLater={handleSaveAndFinishLater}
      />

      <DocumentFlowHelpSheet
        open={helpSheetOpen}
        onClose={() => setHelpSheetOpen(false)}
        sectionKey={category}
        pageTitle={currentCategoryTitle}
      />

      {!helpSheetOpen && (
      <button
        type="button"
        onClick={() => setHelpSheetOpen(true)}
        className={`fixed z-[65] bottom-[max(1.5rem,env(safe-area-inset-bottom))] flex h-14 w-14 items-center justify-center rounded-full bg-emerald-600 text-[26px] font-bold leading-none text-white shadow-lg shadow-emerald-900/20 border border-emerald-700/30 hover:bg-emerald-700 cursor-pointer transition-colors ${
          isRTL ? "left-[max(1.5rem,env(safe-area-inset-left))]" : "right-[max(1.5rem,env(safe-area-inset-right))]"
        }`}
        aria-label={t("docFlow.helpFabAria")}
      >
        ?
      </button>
      )}
    </div>
  );
}
